import { ETransportType } from '../../common/types';
import { TransportId, TransportMode } from './types';

export const TRANSPORT_MODES_EMISSIONS: Record<TransportId, TransportMode> = {
  [TransportId.PLANE]: {
    name: 'Avion',
    ecvs: [
      {
        display: { min: 500, max: 1000 },
        empreinteId: 28130,
        subtitle: 'Court courrier',
        ecv: [
          { id: 5, value: 0.00038 },
          { id: 6, value: 0.1412 },
          { id: 7, value: 0.117 },
        ],
      },
      {
        display: { min: 1001, max: 3500 },
        empreinteId: 28132,
        subtitle: 'Moyen courrier',
        ecv: [
          { id: 5, value: 0.00036 },
          { id: 6, value: 0.1024 },
          { id: 7, value: 0.0848 },
        ],
      },
      {
        empreinteId: 28134,
        subtitle: 'Long courrier',
        display: { min: 3501 },
        ecv: [
          { id: 5, value: 0.00026 },
          { id: 6, value: 0.08299999999999999 },
          { id: 7, value: 0.0687 },
        ],
      },
    ],
    emoji: '✈️',
  },
  [TransportId.TGV]: {
    name: 'TGV',
    ecv: [
      { id: 5, value: 0.00063 },
      { id: 6, value: 0.0023 },
    ],
    emoji: '🚅',
  },
  [TransportId.INTERCITES]: {
    name: 'Intercités',
    ecv: [
      { id: 5, value: 0.00318 },
      { id: 6, value: 0.0058 },
    ],
    emoji: '🚈',
  },
  [TransportId.THERMAL_CAR]: {
    name: 'Voiture',
    ecv: [
      { value: 0.192, id: 6 },
      { value: 0.0256, id: 5 },
    ],
    emoji: '🚗',
    subtitle: 'Moteur thermique',
    carpool: true,
  },
  [TransportId.ELECTRIC_CAR]: {
    name: 'Voiture',
    ecv: [
      { value: 0.0198, id: 6 },
      { value: 0.0836, id: 5 },
    ],
    emoji: '🚗',
    subtitle: 'Moteur électrique',
    carpool: true,
  },
  [TransportId.COACH]: {
    name: 'Autocar',
    ecv: [
      { value: 0.025, id: 6 },
      {
        value: 0.00442130627,
        _comment:
          '// = (Valeur en kgCO<sub>2</sub>e/kg de véhicule * Masse type) / Nombre de passagers / Durée de vie en km = (4 * 12000) / 30 / 361884',
        id: 5,
      },
    ],
    emoji: '🚐',
  },
  [TransportId.BIKE_OR_WALK]: { total: 0, name: 'Vélo ou marche', emoji: '🚶‍♀️' },
  [TransportId.ELECTRIC_BIKE_OR_SCOOTER]: {
    name: 'Vélo (ou trottinette) à assistance électrique',
    ecv: [
      { id: 5, value: 0.00872 },
      { id: 6, value: 0.00223 },
    ],
    emoji: '🚴',
  },
  [TransportId.THERMAL_BUS]: {
    name: 'Bus',
    ecv: [
      { id: 5, value: 0.0089 },
      { id: 6, value: 0.1043 },
    ],
    emoji: '🚌',
    subtitle: 'Moteur thermique',
  },
  [TransportId.TRAMWAY]: {
    name: 'Tramway',
    ecv: [
      { id: 5, value: 0.00048 },
      { id: 6, value: 0.0038 },
    ],
    emoji: '🚌',
  },
  [TransportId.METRO]: {
    name: 'Métro',
    ecv: [
      { id: 5, value: 0.00024 },
      { id: 6, value: 0.0042 },
    ],
    emoji: '🚇',
  },
  [TransportId.SCOOTER_OR_LIGHT_MOTORCYCLES]: {
    name: 'Scooter ou moto légère',
    ecv: [
      { id: 5, value: 0.0159 },
      { id: 6, value: 0.0604 },
    ],
    emoji: '🛵',
  },
  [TransportId.MOTORCYCLES]: {
    name: 'Moto',
    ecv: [
      { id: 5, value: 0.0265 },
      { id: 6, value: 0.1648 },
    ],
    emoji: '🏍️',
  },
  [TransportId.RER_OR_TRANSILIEN]: {
    name: 'RER ou Transilien',
    ecv: [
      { id: 5, value: 0.00318 },
      { id: 6, value: 0.0066 },
    ],
    emoji: '🚃',
  },
  [TransportId.TER]: {
    name: 'TER',
    ecv: [
      { id: 5, value: 0.00479 },
      { id: 6, value: 0.0229 },
    ],
    emoji: '🚈',
  },
  [TransportId.ELECTRIC_BUS]: {
    name: 'Bus',
    ecv: [
      { id: 5, value: 0.0122 },
      { id: 6, value: 0.0095 },
    ],
    emoji: '🚌',
    subtitle: 'Moteur électrique',
  },
  [TransportId.GNV_BUS]: {
    name: 'Bus',
    ecv: [
      { id: 5, value: 0.0089 },
      { id: 6, value: 0.1128 },
    ],
    emoji: '🚌',
    subtitle: 'GNV',
  },
};

export const getTransportId = (pageTransportType: ETransportType) => {
  return {
    [ETransportType.BUS]: TransportId.THERMAL_BUS,
    [ETransportType.TRAIN]: TransportId.TGV,
  }[pageTransportType];
};

export const computeCo2g = (distanceInKm: number, transportModeId: string) => {
  const transportMode = TRANSPORT_MODES_EMISSIONS[transportModeId];

  if (!transportMode) {
    throw new Error('Unknown transport mode: ' + transportMode);
  }

  let values = [{ value: transportMode.total || 0 }];

  if (transportMode.ecvs) {
    const currentECV = transportMode.ecvs.find((value) =>
      value.display?.max ? value.display.max >= distanceInKm : true,
    );

    if (currentECV) {
      values = currentECV.ecv;
    }
  } else if (transportMode.ecv) {
    values = transportMode.ecv;
  }

  const value = values.reduce((sum, current) => sum + current.value, 0);

  const emissions = {
    gco2e: value * distanceInKm * 1000,
    kgco2e: value * distanceInKm,
    tco2e: (value * distanceInKm) / 1000,
  };

  return emissions.gco2e;
};
